import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { isBrowserAuthConnection, isEqual } from 'vet-bones/utils';
import {
  usePortalCsrfToken,
  usePortalHeap,
  usePortalRollbar,
  usePortalUserflow,
  useVerifyPortalAuth,
} from 'vet-bones/utils/portal';

import { dataSourcesStardogRequestDispatchers } from 'src/actions/data-sources/actionCreators';
import { ROUTE } from 'src/constants/routes';
import { connectionAsyncActions } from 'src/store/connections/connections.slice';
import { setupPortalConnection } from 'src/store/connections/connectToStardog';
import { uiSlice } from 'src/store/ui/ui.slice';
import { useDefaultPortalConnection } from 'src/utils/hooks/useDefaultPortalConnection';
import { useDidValueChange } from 'src/utils/hooks/useDidValueChange';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';

/**
 * Define the app-level hooks as a child of the App to prevent
 * the App (and all of its children) from unnecessarily re-rendering
 * every time a dependency of the app-level hooks changes.
 */
export const AppHooks: React.VFC = () => {
  const dispatch = useReduxDispatch();
  const location = useLocation();
  const didLocationChange = useDidValueChange(location, isEqual);
  const defaultPortalConnection = useDefaultPortalConnection();
  const didDefaultConnectionChange = useDidValueChange(
    defaultPortalConnection,
    isEqual
  );
  useVerifyPortalAuth();
  usePortalHeap();
  usePortalUserflow();
  usePortalRollbar();
  usePortalCsrfToken();

  const defaultConnection = useDefaultPortalConnection();

  const didConnection = React.useRef(false);
  React.useEffect(() => {
    if (didConnection.current || !defaultConnection) {
      return;
    }
    didConnection.current = true;

    dispatch(
      setupPortalConnection(defaultConnection, {
        shouldNotNotifySuccess: true,
      })
    );
  }, [defaultConnection, didConnection, dispatch]);

  // always refresh default portal connection data
  React.useEffect(() => {
    if (
      didDefaultConnectionChange &&
      defaultPortalConnection &&
      (defaultPortalConnection.token ||
        defaultPortalConnection.password ||
        isBrowserAuthConnection(defaultPortalConnection))
    ) {
      dispatch(
        connectionAsyncActions.requestServerStatus({
          connection: defaultPortalConnection,
          params: { databases: false },
        })
      );
      dispatch(
        connectionAsyncActions.requestServerProperties({
          connection: defaultPortalConnection,
          params: ['catalog.database', 'dbms.query.log.database'],
        })
      );
      dataSourcesStardogRequestDispatchers.requestDataSourceTypeDescription(
        defaultPortalConnection
      );
    }
  }, [didDefaultConnectionChange, defaultPortalConnection, dispatch]);

  React.useEffect(() => {
    if (didLocationChange) {
      dispatch(
        uiSlice.actions.changedLocation({ route: location.pathname as ROUTE })
      );
    }
  }, [dispatch, didLocationChange, location]);

  return null;
};
